import { unwrapConnection } from "@utils/utils";

import {
  ApiReportReportTypeChoices,
  ApiReportSectionFieldsFieldTypeChoices,
  ReportSectionsType,
} from "@api/graphql/types-and-hooks";
import {
  useIsReportBaseTemplate,
  useIsReportCompleted,
  useIsReportHandlebars,
  useIsReviewPage,
} from "@components/ReportField/capabilities/flags";
import { APPENDIX_PAGE_LOCATION } from "@components/ReportField/consts";
import { useReport } from "@providers/ReportProvider";

// Whether "Delete" shows, off review and not completed. Reproduces the legacy
// per-surface gating:
//   assessment  (InitialAssessment)          -> handlebars export
//   evaluation  (DiagnosisEvaluation + step) -> base template or appendix
//   clinical    (Reassessment | DE + single) -> handlebars, or single-page appendix
// plus the standing rule that a section holding the Treatment Plan field can't be
// deleted — users do it accidentally and there's no way to get it back. Keyed on the
// field the section contains (not its identifier) so the guard follows the field even
// after it's moved to another section (PROA-2105).
export const useCanDeleteSection = (section: ReportSectionsType): boolean => {
  const { report, viewMode } = useReport();
  const isHandlebars = useIsReportHandlebars();
  const isBaseTemplate = useIsReportBaseTemplate();
  const isReviewPage = useIsReviewPage();
  const isCompleted = useIsReportCompleted();

  const sectionContainsTreatmentPlanField = !!unwrapConnection(section.sectionField)?.some(
    (sf) => sf?.fieldType === ApiReportSectionFieldsFieldTypeChoices.TreatmentPlan,
  );
  if (sectionContainsTreatmentPlanField) return false;
  if (isReviewPage || isCompleted) return false;

  if (report.reportType === ApiReportReportTypeChoices.DiagnosisEvaluation && viewMode === "step") {
    return isBaseTemplate || section.pageLocation === APPENDIX_PAGE_LOCATION;
  }

  // clinical (Reassessment | Diagnosis Evaluation single-page)
  if (isHandlebars) return true;
  const isDiagnosisSinglePage =
    report.reportType === ApiReportReportTypeChoices.DiagnosisEvaluation && viewMode === "single";
  return isDiagnosisSinglePage && section.pageLocation === APPENDIX_PAGE_LOCATION;
};
